import { Modal } from '../../components/ui/Modal';
import { useAuth } from '../auth/auth-context';

import { DisplayOptions } from './DisplayOptions';
import { themeLabel, useUiPreferences, viewLabel } from './ui-preferences';

/**
 * Réglage personnel de l'affichage. Chaque choix s'applique immédiatement ;
 * « Revenir au réglage de mon organisation » efface la préférence personnelle.
 */
export function DisplaySettingsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const { data, uiTheme, automationView, tileColumns, hideQuickAccess, update } =
    useUiPreferences();
  // L'accès rapide n'existe que sur l'accueil des comptes clients.
  const isClient = user?.role === 'CLIENT_ADMIN' || user?.role === 'CLIENT_USER';
  const hasOwnChoice = !!(
    data?.user.uiTheme ||
    data?.user.automationView ||
    data?.user.tileColumns
  );
  const orgTheme = data?.organization.uiTheme ?? 'MARINE';
  const orgView = data?.organization.automationView ?? 'LIST';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Affichage"
      size="lg"
      footer={
        <button
          type="button"
          onClick={onClose}
          className="rounded-full bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Terminé
        </button>
      }
    >
      <p className="mb-4 text-sm text-slate-600">
        Ces choix ne concernent que vous et s’appliquent tout de suite.
      </p>
      <DisplayOptions
        idPrefix="me"
        uiTheme={uiTheme}
        automationView={automationView}
        tileColumns={tileColumns}
        onThemeChange={(v) => update.mutate({ uiTheme: v })}
        onViewChange={(v) => update.mutate({ automationView: v })}
        onTileColumnsChange={(v) => update.mutate({ tileColumns: v })}
        disabled={update.isPending}
      />
      {isClient && (
        <label className="mt-5 flex items-start gap-3 border-t border-slate-100 pt-4 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={!hideQuickAccess}
            onChange={(e) => update.mutate({ hideQuickAccess: !e.target.checked })}
            disabled={update.isPending}
          />
          <span>
            <span className="font-medium text-slate-900">Accès rapide sur l’accueil</span>
            <span className="block text-xs text-slate-500">
              Vos automatisations favorites, ou les plus utilisées, en haut de la Synthèse.
            </span>
          </span>
        </label>
      )}
      {update.isError && (
        <p role="alert" className="mt-3 text-xs text-red-600">
          Le changement n’a pas été enregistré. Vérifiez votre connexion et réessayez.
        </p>
      )}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4 text-xs text-slate-500">
        <span>
          Réglage de votre organisation : {themeLabel(orgTheme)} · {viewLabel(orgView)}
        </span>
        {hasOwnChoice && (
          <button
            type="button"
            onClick={() =>
              update.mutate({ uiTheme: null, automationView: null, tileColumns: null })
            }
            disabled={update.isPending}
            className="font-medium text-brand hover:underline disabled:opacity-50"
          >
            Revenir au réglage de mon organisation
          </button>
        )}
      </div>
    </Modal>
  );
}
